// =============================================
// SERVICE: Fila Offline para Bipagens (IndexedDB)
// Armazena leituras localmente quando offline e
// sincroniza automaticamente ao reconectar.
// Tambem gerencia conferencias criadas offline.
// =============================================

import { registrarLeituraConferente, criarConferenciaConferente, getConferenteToken } from './conferente';

const DB_NAME = 'confereai_offline';
const DB_VERSION = 2;
const STORE_LEITURAS = 'leituras_pendentes';
const STORE_CACHE = 'conferencias_cache';
const STORE_CONFERENCIAS_PENDENTES = 'conferencias_pendentes';
const MAX_TENTATIVAS = 5;

export interface LeituraPendente {
  id?: number;
  conferencia_id: number;
  tipo_leitura: string;
  codigo_lido: string;
  quantidade: number;
  lido_em: string;
  dados_decodificados?: Record<string, unknown>;
  tentativas: number;
  status: 'pendente' | 'enviando' | 'erro' | 'falha_permanente';
  erro_msg?: string;
}

export interface ConferenciaCache {
  id: number;
  data: any;
  cached_at: string;
}

export interface ConferenciaPendente {
  id?: number; // auto-increment local ID
  temp_id: number; // ID temporario negativo usado para vincular leituras
  tipo: string;
  cliente_id?: number;
  placa_veiculo?: string;
  transportadora?: string;
  manifesto?: string;
  rota?: string;
  observacoes?: string;
  criado_em: string;
  tentativas: number;
  status: 'pendente' | 'enviando' | 'sincronizado' | 'falha_permanente';
  server_id?: number; // ID real recebido do servidor apos sync
  erro_msg?: string;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORE_LEITURAS)) {
        const store = db.createObjectStore(STORE_LEITURAS, { keyPath: 'id', autoIncrement: true });
        store.createIndex('conferencia_id', 'conferencia_id', { unique: false });
        store.createIndex('status', 'status', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        db.createObjectStore(STORE_CACHE, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(STORE_CONFERENCIAS_PENDENTES)) {
        const store = db.createObjectStore(STORE_CONFERENCIAS_PENDENTES, { keyPath: 'id', autoIncrement: true });
        store.createIndex('temp_id', 'temp_id', { unique: true });
        store.createIndex('status', 'status', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// =============================================
// LEITURAS PENDENTES (bipagens offline)
// =============================================

export async function salvarLeituraPendente(leitura: Omit<LeituraPendente, 'id' | 'tentativas' | 'status'>): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_LEITURAS, 'readwrite');
    const store = tx.objectStore(STORE_LEITURAS);

    const record: Omit<LeituraPendente, 'id'> = {
      ...leitura,
      tentativas: 0,
      status: 'pendente',
    };

    let resultId: number;
    const req = store.add(record);
    req.onsuccess = () => { resultId = req.result as number; };
    req.onerror = () => { db.close(); reject(req.error); };
    tx.oncomplete = () => { db.close(); resolve(resultId); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

export async function listarLeiturasPendentes(conferenciaId?: number): Promise<LeituraPendente[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_LEITURAS, 'readonly');
    const store = tx.objectStore(STORE_LEITURAS);

    let req: IDBRequest;
    if (conferenciaId !== undefined) {
      const index = store.index('conferencia_id');
      req = index.getAll(conferenciaId);
    } else {
      req = store.getAll();
    }

    let result: LeituraPendente[];
    req.onsuccess = () => { result = req.result; };
    req.onerror = () => { db.close(); reject(req.error); };
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function contarLeiturasPendentes(): Promise<number> {
  const todas = await listarLeiturasPendentes();
  return todas.filter(l => l.status !== 'falha_permanente').length;
}

export async function removerLeituraPendente(id: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_LEITURAS, 'readwrite');
    const store = tx.objectStore(STORE_LEITURAS);
    store.delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

export async function atualizarLeituraPendente(id: number, updates: Partial<LeituraPendente>): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_LEITURAS, 'readwrite');
    const store = tx.objectStore(STORE_LEITURAS);
    const getReq = store.get(id);

    getReq.onsuccess = () => {
      const record = getReq.result;
      if (!record) return;
      store.put({ ...record, ...updates });
    };
    getReq.onerror = () => { db.close(); reject(getReq.error); };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

// =============================================
// CACHE DE CONFERENCIAS (para offline)
// =============================================

export async function salvarConferenciaCache(id: number, data: any): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    const store = tx.objectStore(STORE_CACHE);
    const record: ConferenciaCache = {
      id,
      data,
      cached_at: new Date().toISOString(),
    };
    store.put(record);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

export async function buscarConferenciaCache(id: number): Promise<any | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readonly');
    const store = tx.objectStore(STORE_CACHE);
    const req = store.get(id);
    let result: any = null;
    req.onsuccess = () => {
      const record = req.result as ConferenciaCache | undefined;
      result = record ? record.data : null;
    };
    req.onerror = () => { db.close(); reject(req.error); };
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

// =============================================
// CONFERENCIAS PENDENTES (criadas offline)
// Usa IDs temporarios negativos para vincular
// leituras ate a sincronizacao.
// =============================================

let nextTempId = -1;
let tempIdInicializado = false;

async function inicializarTempId(): Promise<void> {
  if (tempIdInicializado) return;
  try {
    const pendentes = await listarConferenciasPendentes();
    if (pendentes.length > 0) {
      const menorId = Math.min(...pendentes.map(c => c.temp_id));
      nextTempId = Math.min(nextTempId, menorId - 1);
    }
    tempIdInicializado = true;
  } catch {
    // Se falhar, usar o padrao
  }
}

export async function gerarTempConferenciaId(): Promise<number> {
  await inicializarTempId();
  return nextTempId--;
}

export async function salvarConferenciaPendente(
  conferencia: Omit<ConferenciaPendente, 'id' | 'status' | 'criado_em' | 'tentativas'>
): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONFERENCIAS_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORE_CONFERENCIAS_PENDENTES);

    const record: Omit<ConferenciaPendente, 'id'> = {
      ...conferencia,
      criado_em: new Date().toISOString(),
      tentativas: 0,
      status: 'pendente',
    };

    store.add(record);
    tx.oncomplete = () => { db.close(); resolve(conferencia.temp_id); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

export async function listarConferenciasPendentes(): Promise<ConferenciaPendente[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONFERENCIAS_PENDENTES, 'readonly');
    const store = tx.objectStore(STORE_CONFERENCIAS_PENDENTES);
    const req = store.getAll();
    let result: ConferenciaPendente[];
    req.onsuccess = () => { result = req.result; };
    req.onerror = () => { db.close(); reject(req.error); };
    tx.oncomplete = () => { db.close(); resolve(result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

export async function contarConferenciasPendentes(): Promise<number> {
  const todas = await listarConferenciasPendentes();
  return todas.filter(c => c.status !== 'sincronizado' && c.status !== 'falha_permanente').length;
}

async function removerConferenciaPendente(id: number): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONFERENCIAS_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORE_CONFERENCIAS_PENDENTES);
    store.delete(id);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

async function atualizarConferenciaPendente(id: number, updates: Partial<ConferenciaPendente>): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_CONFERENCIAS_PENDENTES, 'readwrite');
    const store = tx.objectStore(STORE_CONFERENCIAS_PENDENTES);
    const getReq = store.get(id);
    getReq.onsuccess = () => {
      const record = getReq.result;
      if (!record) return;
      store.put({ ...record, ...updates });
    };
    getReq.onerror = () => { db.close(); reject(getReq.error); };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Transacao abortada')); };
  });
}

// Atualizar conferencia_id de leituras quando o ID real e recebido
async function remapearLeiturasConferencia(tempId: number, realId: number): Promise<void> {
  const leituras = await listarLeiturasPendentes(tempId);
  for (const leitura of leituras) {
    if (leitura.id) {
      await atualizarLeituraPendente(leitura.id, { conferencia_id: realId });
    }
  }
}

// =============================================
// SINCRONIZACAO
// Usa a API do conferente diretamente para enviar
// conferencias e leituras pendentes ao servidor.
// =============================================

let syncInProgress = false;

async function enviarLeitura(leitura: LeituraPendente): Promise<boolean> {
  if (!getConferenteToken()) return false;

  try {
    const payload: any = {
      tipo_leitura: leitura.tipo_leitura,
      codigo_lido: leitura.codigo_lido,
      quantidade: leitura.quantidade,
    };
    if (leitura.dados_decodificados) {
      payload.dados_decodificados = leitura.dados_decodificados;
    }
    if (leitura.lido_em) {
      payload.lido_em = leitura.lido_em;
    }
    const result = await registrarLeituraConferente(leitura.conferencia_id, payload);
    return result.success;
  } catch {
    return false;
  }
}

// Resetar itens presos em 'enviando' (podem ficar presos apos crash/tab close)
async function resetarItensEnviando(): Promise<void> {
  try {
    const todasLeituras = await listarLeiturasPendentes();
    for (const l of todasLeituras) {
      if (l.status === 'enviando' && l.id) {
        await atualizarLeituraPendente(l.id, { status: 'pendente' });
      }
    }
    const todasConf = await listarConferenciasPendentes();
    for (const c of todasConf) {
      if (c.status === 'enviando' && c.id) {
        await atualizarConferenciaPendente(c.id, { status: 'pendente' });
      }
    }
  } catch {
    // Nao bloquear sync por causa de reset
  }
}

export async function sincronizarPendentes(): Promise<{ sincronizados: number; erros: number; conferencias_criadas: number }> {
  if (syncInProgress) return { sincronizados: 0, erros: 0, conferencias_criadas: 0 };
  if (!navigator.onLine) return { sincronizados: 0, erros: 0, conferencias_criadas: 0 };
  if (!getConferenteToken()) return { sincronizados: 0, erros: 0, conferencias_criadas: 0 };

  syncInProgress = true;
  let sincronizados = 0;
  let erros = 0;
  let conferencias_criadas = 0;

  try {
    // PASSO 0: Resetar itens presos em 'enviando'
    await resetarItensEnviando();

    // PASSO 1: Sincronizar conferencias pendentes (criadas offline)
    const confPendentes = await listarConferenciasPendentes();
    const confAEnviar = confPendentes.filter(c => c.status === 'pendente');
    const mapeamentoIds: { tempId: number; realId: number }[] = [];

    for (const conf of confAEnviar) {
      if (!conf.id) continue;

      if (conf.tentativas >= MAX_TENTATIVAS) {
        await atualizarConferenciaPendente(conf.id, {
          status: 'falha_permanente',
          erro_msg: `Excedeu ${MAX_TENTATIVAS} tentativas`,
        });
        erros++;
        continue;
      }

      try {
        await atualizarConferenciaPendente(conf.id, { status: 'enviando' });
        const result = await criarConferenciaConferente({
          tipo: conf.tipo,
          cliente_id: conf.cliente_id,
          placa_veiculo: conf.placa_veiculo,
          transportadora: conf.transportadora,
          manifesto: conf.manifesto,
          rota: conf.rota,
          observacoes: conf.observacoes,
        });

        if (result.success && result.conferencia) {
          await remapearLeiturasConferencia(conf.temp_id, result.conferencia.id);
          mapeamentoIds.push({ tempId: conf.temp_id, realId: result.conferencia.id });
          await atualizarConferenciaPendente(conf.id, {
            status: 'sincronizado',
            server_id: result.conferencia.id,
          });
          conferencias_criadas++;
        }
      } catch (err: any) {
        try {
          await atualizarConferenciaPendente(conf.id, {
            status: 'pendente',
            tentativas: (conf.tentativas || 0) + 1,
            erro_msg: err.message || 'Erro ao criar conferencia',
          });
        } catch { /* nao bloquear sync */ }
        erros++;
      }
    }

    // PASSO 2: Remapear leituras de TODAS conferencias sincronizadas (incluindo ciclos anteriores)
    const todasConfAtualizadas = await listarConferenciasPendentes();
    for (const conf of todasConfAtualizadas) {
      if (conf.status === 'sincronizado' && conf.server_id) {
        await remapearLeiturasConferencia(conf.temp_id, conf.server_id);
      }
    }

    // PASSO 3: Sincronizar leituras pendentes
    const pendentes = await listarLeiturasPendentes();
    const aEnviar = pendentes.filter(l =>
      (l.status === 'pendente' || l.status === 'erro') && l.conferencia_id > 0
    );

    for (const leitura of aEnviar) {
      if (!leitura.id) continue;

      if (leitura.tentativas >= MAX_TENTATIVAS) {
        await atualizarLeituraPendente(leitura.id, {
          status: 'falha_permanente',
          erro_msg: `Excedeu ${MAX_TENTATIVAS} tentativas`,
        });
        erros++;
        continue;
      }

      try {
        await atualizarLeituraPendente(leitura.id, { status: 'enviando' });
        const sucesso = await enviarLeitura(leitura);

        if (sucesso) {
          await removerLeituraPendente(leitura.id);
          sincronizados++;
        } else {
          await atualizarLeituraPendente(leitura.id, {
            status: 'erro',
            tentativas: leitura.tentativas + 1,
            erro_msg: 'Falha ao enviar',
          });
          erros++;
        }
      } catch (err: any) {
        if (leitura.id) {
          await atualizarLeituraPendente(leitura.id, {
            status: 'erro',
            tentativas: leitura.tentativas + 1,
            erro_msg: err.message || 'Erro desconhecido',
          });
        }
        erros++;
      }
    }

    // PASSO 4: Limpar conferencias sincronizadas (somente se leituras foram enviadas)
    const confSincronizadas = await listarConferenciasPendentes();
    const todasLeituras = await listarLeiturasPendentes();
    for (const conf of confSincronizadas) {
      if (conf.status === 'sincronizado' && conf.id) {
        const leiturasConf = todasLeituras.filter(l =>
          l.conferencia_id === conf.temp_id || l.conferencia_id === conf.server_id
        );
        const leiturasPendentesConf = leiturasConf.filter(l =>
          l.status === 'pendente' || l.status === 'enviando' || l.status === 'erro'
        );
        if (leiturasPendentesConf.length === 0) {
          await removerConferenciaPendente(conf.id);
        }
      }
    }
  } finally {
    syncInProgress = false;
  }

  return { sincronizados, erros, conferencias_criadas };
}
